import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Group, Loader, Modal, Stack, Table, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  deleteAbsence,
  downloadAbsenceAttachment,
  fetchAbsences,
  getAbsenceApplicationUrl,
  hasAbsenceApplication,
  submitAbsence,
  withdrawAbsence,
  type Absence,
} from "../api/client";
import { useBadges } from "../hooks/BadgesContext";
import { formatHours } from "../utils/format";
import { AbsenceForm } from "./AbsenceForm";
import { AbsenceRouteModal } from "./AbsenceRouteModal";
import { StatusBadge } from "./StatusBadge";

dayjs.locale("ru");

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD.MM.YYYY") : iso;
}

function formatRange(start: string, end: string): string {
  return `${formatDate(start)} — ${formatDate(end)}`;
}

export function AbsenceList() {
  // На телефоне модалка формы разворачивается на весь экран (иначе size="lg"
  // шире вьюпорта и подписи обрезаются слева), а таблица едет со скроллом.
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Absence | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [routeId, setRouteId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchAbsences();
      setAbsences(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  const handleCreate = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((absence: Absence) => {
    setEditing(absence);
    setFormOpen(true);
  }, []);

  // US-59 #8: после mutating action refetch бейджей сразу.
  const { refresh: refreshBadges } = useBadges();

  const handleDelete = useCallback(
    async (absence: Absence) => {
      setBusyId(absence.id);
      try {
        await deleteAbsence(absence.id);
        void refreshBadges();
        await reload();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [reload, refreshBadges],
  );

  const handleSubmit = useCallback(
    async (absence: Absence) => {
      setBusyId(absence.id);
      try {
        await submitAbsence(absence.id);
        void refreshBadges();
        await reload();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [reload, refreshBadges],
  );

  const handleWithdraw = useCallback(
    async (absence: Absence) => {
      setBusyId(absence.id);
      try {
        await withdrawAbsence(absence.id);
        void refreshBadges();
        await reload();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [reload, refreshBadges],
  );

  const handleSaved = useCallback(async () => {
    setFormOpen(false);
    setEditing(null);
    await reload();
  }, [reload]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={600}>Мои отсутствия</Text>
        <Button onClick={handleCreate}>Создать заявку</Button>
      </Group>

      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : absences.length === 0 ? (
        <Stack align="center" py="xl">
          <Text c="dimmed">Заявок ещё нет.</Text>
        </Stack>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          {/* min-width: без неё на телефоне колонки сжимаются и «Действия»
              (Маршрут/Заявление) уезжают за экран без возможности доскроллить. */}
          <Table striped withTableBorder withColumnBorders miw={760}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Тип</Table.Th>
                <Table.Th>Даты</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Дней (раб. / кал.)</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Часов</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th>Действия</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {absences.map((a) => {
                const busy = busyId === a.id;
                const isDraft = a.status === "draft";
                const isReturned = a.status === "returned";
                // US-107: со ступени РП заявку тоже можно отозвать — иначе она
                // держит дни занятыми, пока руководитель проекта до неё не дойдёт.
                const isPending =
                  a.status === "submitted_to_pm" ||
                  a.status === "submitted" ||
                  a.status === "approved_by_lr";
                return (
                  <Table.Tr key={a.id}>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <span>{a.absence_type_display}</span>
                        {/* US-66: скрепка-индикатор + быстрое скачивание. */}
                        {a.has_attachment && (
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            px={4}
                            onClick={() =>
                              downloadAbsenceAttachment(
                                a.id,
                                a.attachment_name ?? "absence.pdf",
                              ).catch((err: unknown) =>
                                setError(
                                  err instanceof Error ? err.message : "Не удалось скачать файл",
                                ),
                              )
                            }
                            title={a.attachment_name ?? "Скачать файл-заявление"}
                            data-testid={`absence-attach-${a.id}`}
                          >
                            📎
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{formatRange(a.start_date, a.end_date)}</Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      {a.working_days}
                      {a.calendar_days != null ? ` / ${a.calendar_days}` : ""}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>{formatHours(a.hours)}</Table.Td>
                    <Table.Td>
                      <StatusBadge entity="absence" code={a.status} label={a.status_display} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => setRouteId(a.id)}
                          data-testid={`absence-route-${a.id}`}
                        >
                          Маршрут
                        </Button>
                        {hasAbsenceApplication(a.absence_type) && (
                          <Button
                            component="a"
                            href={getAbsenceApplicationUrl(a.id)}
                            download
                            size="xs"
                            variant="subtle"
                            color="teal"
                            title="Скачать заявление в Word (можно поправить и распечатать)"
                            data-testid={`absence-application-${a.id}`}
                          >
                            Заявление
                          </Button>
                        )}
                        {isDraft && (
                          <>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => handleEdit(a)}
                              disabled={busy}
                            >
                              Редактировать
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              onClick={() => handleDelete(a)}
                              loading={busy}
                              disabled={busy}
                            >
                              Удалить
                            </Button>
                            <Button
                              size="xs"
                              onClick={() => handleSubmit(a)}
                              loading={busy}
                              disabled={busy}
                            >
                              Отправить
                            </Button>
                          </>
                        )}
                        {isReturned && (
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => handleEdit(a)}
                            disabled={busy}
                          >
                            Редактировать и переотправить
                          </Button>
                        )}
                        {isPending && (
                          <Button
                            size="xs"
                            variant="light"
                            color="orange"
                            onClick={() => handleWithdraw(a)}
                            loading={busy}
                            disabled={busy}
                            data-testid={`absence-withdraw-${a.id}`}
                          >
                            Отозвать
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      <Modal
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        title={editing ? "Редактирование заявки" : "Новая заявка на отсутствие"}
        size="lg"
        centered={!isMobile}
        fullScreen={isMobile}
      >
        <AbsenceForm
          initial={editing}
          onSaved={handleSaved}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      </Modal>

      <AbsenceRouteModal
        absenceId={routeId}
        opened={routeId != null}
        onClose={() => setRouteId(null)}
      />
    </Stack>
  );
}
